import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import { useJanusCall } from './useJanusCall';

const phaseLabel: Record<string, string> = {
  requesting: 'Calling…',
  ringing: 'Ringing…',
  connecting: 'Connecting…',
  in_call: '',
  ended: 'Call ended',
};

const endReasonLabel: Record<string, string> = {
  REJECTED: 'Declined',
  NO_ANSWER: 'No answer',
  DISCONNECTED: 'Disconnected',
  COMPLETED: 'Call ended',
};

// In-page v2 call screen (OI-1564). Renders whenever redux has an activeCall;
// the controller hook owns the Janus session + media. Replaces the legacy
// /video-call popup.
export default function VideoCallModalV2() {
  const { activeCall, localStream, remoteStream, errorMsg, hangup } =
    useJanusCall();
  const localRef = useRef<HTMLVideoElement>(null);
  const remoteRef = useRef<HTMLVideoElement>(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);

  useEffect(() => {
    if (localRef.current) localRef.current.srcObject = localStream;
  }, [localStream]);
  useEffect(() => {
    if (remoteRef.current) remoteRef.current.srcObject = remoteStream;
  }, [remoteStream]);

  const toggleMic = () => {
    const next = !micOn;
    localStream?.getAudioTracks().forEach((t) => (t.enabled = next));
    setMicOn(next);
  };
  const toggleCam = () => {
    const next = !camOn;
    localStream?.getVideoTracks().forEach((t) => (t.enabled = next));
    setCamOn(next);
  };

  if (!activeCall) return null;

  const status =
    activeCall.phase === 'ended'
      ? (endReasonLabel[activeCall.endReason ?? 'COMPLETED'] ?? 'Call ended')
      : (phaseLabel[activeCall.phase] ?? '');

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        bgcolor: '#0b0f14',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        <video
          ref={remoteRef}
          autoPlay
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        {!remoteStream && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              gap: 1,
            }}
          >
            <Typography variant="h5">{activeCall.peerName}</Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              {status || 'Connecting…'}
            </Typography>
          </Box>
        )}

        <Box
          component="video"
          ref={localRef}
          autoPlay
          playsInline
          muted
          sx={{
            position: 'absolute',
            bottom: 24,
            right: 24,
            width: 200,
            maxWidth: '30vw',
            borderRadius: 2,
            border: '2px solid rgba(255,255,255,0.4)',
            bgcolor: 'black',
            objectFit: 'cover',
          }}
        />

        {remoteStream && status && (
          <Typography
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              color: 'white',
              opacity: 0.85,
            }}
          >
            {activeCall.peerName} — {status}
          </Typography>
        )}

        {errorMsg && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: 'rgba(229,57,53,0.95)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography>{errorMsg}</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={hangup}
              sx={{ color: 'white', borderColor: 'white' }}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          py: 3,
          bgcolor: '#0b0f14',
        }}
      >
        <IconButton
          onClick={toggleMic}
          sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }}
        >
          {micOn ? <MicIcon /> : <MicOffIcon />}
        </IconButton>
        <IconButton
          onClick={hangup}
          sx={{ bgcolor: '#e53935', color: 'white', px: 3 }}
        >
          <CallEndIcon />
        </IconButton>
        <IconButton
          onClick={toggleCam}
          sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }}
        >
          {camOn ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}
